import { injectable } from 'tsyringe';
import type {
    GovernanceActivityEntry,
    GovernanceIssueAttachment,
    GovernanceIssueAttachmentContent,
    GovernanceIssueDetail,
    GovernanceIssueEvent,
    GovernanceIssueKind,
    GovernanceIssueNote,
    GovernanceIssueState,
    GovernanceIssueStatus,
    UpdateGovernanceIssueInput,
} from '@bmai/context-api-client';
import { contextClient } from '@/shared/ContextApiClient';

export type {
    GovernanceActivityEntry,
    GovernanceIssueAttachment,
    GovernanceIssueAttachmentContent,
    GovernanceIssueDetail,
    GovernanceIssueEvent,
    GovernanceIssueKind,
    GovernanceIssueNote,
    GovernanceIssueState,
    GovernanceIssueStatus,
    UpdateGovernanceIssueInput,
};

/**
 * Wraps the generated governance-issue API — durable, team-shared triage state
 * (status, flag, assignee, resolution, notes, audit trail, per-turn dismissals) for
 * governance issues, keyed by (`kind`, `ref`). Consumed by {@link GovernanceIssueStore}.
 */
@injectable()
export class GovernanceIssuesService {
    /** All tracked issue states for the tenant; issues never written to are absent. */
    list(): Promise<GovernanceIssueState[]> {
        return contextClient.governanceApi.getIssues();
    }

    /** Recent governance audit events across all issues, newest first. */
    activity(limit = 200): Promise<GovernanceActivityEntry[]> {
        return contextClient.governanceApi.getActivity({ limit: String(limit) });
    }

    /** Full detail (state + notes + audit events + dismissed turns) for one issue. */
    detail(kind: GovernanceIssueKind, ref: string): Promise<GovernanceIssueDetail> {
        return contextClient.governanceApi.getIssuesByKindByRef({ kind, ref });
    }

    /** Patch triage state (status / flag / assignee); returns the fresh detail. */
    update(kind: GovernanceIssueKind, ref: string, input: UpdateGovernanceIssueInput): Promise<GovernanceIssueDetail> {
        return contextClient.governanceApi.patchIssuesByKindByRef({ kind, ref, updateGovernanceIssueInput: input });
    }

    /** Resolve an issue with a documented reason; returns the fresh detail. */
    resolve(kind: GovernanceIssueKind, ref: string, reason: string): Promise<GovernanceIssueDetail> {
        return contextClient.governanceApi.postIssuesByKindByRefResolve({ kind, ref, resolveGovernanceIssueInput: { reason } });
    }

    /** Append a documentation/handling note. */
    async addNote(kind: GovernanceIssueKind, ref: string, body: string): Promise<void> {
        await contextClient.governanceApi.postIssuesByKindByRefNotes({ kind, ref, addGovernanceIssueNoteInput: { body } });
    }

    /** Upload a documentation file (content base64-encoded). */
    async addAttachment(kind: GovernanceIssueKind, ref: string, filename: string, contentType: string, contentBase64: string): Promise<void> {
        await contextClient.governanceApi.postIssuesByKindByRefAttachments({ kind, ref, addGovernanceIssueAttachmentInput: { filename, contentType, contentBase64 } });
    }

    /** Fetch an uploaded documentation file's bytes (base64) for download. */
    attachment(kind: GovernanceIssueKind, ref: string, attachmentId: string): Promise<GovernanceIssueAttachmentContent> {
        return contextClient.governanceApi.getIssuesByKindByRefAttachmentsByAttachmentId({ kind, ref, attachmentId });
    }

    /** Dismiss a single flagged turn as a false positive for this issue. */
    async dismissTurn(kind: GovernanceIssueKind, ref: string, interactionId: string): Promise<void> {
        await contextClient.governanceApi.postIssuesByKindByRefTurnDismissals({ kind, ref, dismissTurnInput: { interactionId } });
    }

    /** Restore a previously dismissed turn. */
    async restoreTurn(kind: GovernanceIssueKind, ref: string, interactionId: string): Promise<void> {
        await contextClient.governanceApi.deleteIssuesByKindByRefTurnDismissalsByInteractionId({ kind, ref, interactionId });
    }
}
